<template>
  <div
    class="dashboard-wrapper"
    :class="{
      'fixed-dashboard':
        showVideoLibrary || showWidgetCreator || allMetricsLoading,
    }"
  >
    <DashboardSkeleton v-if="allMetricsLoading" class="dashboard-skeleton" />
    <banner-locked v-if="bannerLocked" :warning-icon="true">
      <template #text="{ reason }">
        <h3>
          {{ reason }}
        </h3>
      </template>
      <template #button="{ buttonText }">
        {{ buttonText }}
      </template>
    </banner-locked>
    <main
      class="main-content-wrapper"
      :class="{ 'main-content-wrapper-agency': isAgency }"
    >
      <vidjet-header
        class="title"
        :title="$t('dashboard.heading.title')"
        :show-create-button="true"
        :show-video-library-button="true"
        :video-library-creation="videoLibraryCreation"
        :preselected-query="videoFileNameToFilter"
        :show-video-library-prop="showVideoLibrary"
        :dashboard-skeleton-showing="allMetricsLoading"
        @show-video-library="showVideoLibrary = true"
        @open-widget-creator="showWidgetCreator = true"
        @close-video-library="closeVideoLibrary"
        @close-widget-creator="showWidgetCreator = false"
      />

      <section class="dashboard-filters">
        <div class="date-device-filters">
          <dashboard-datepicker
            :first-init-done-prop="firstInitDone"
            :from-date-pick.sync="fromDate"
            :to-date-pick.sync="toDate"
            @dates-changed="updateData"
            :siteId="site._id"
          />
        </div>
        <button class="export-button" @click="handleExportData">
          {{ $t("dashboard.topVideos.export") }} <ExportIcon />
        </button>
      </section>
      <dashboard-frames
        :dashboard-data="dashboardGlobalData"
        :frames="frames"
        :currency="getCurrency"
        :selected-frame="selectedFrame"
        @selectedFrame="selectFrame($event)"
      />
      <div class="expandable-wrapper">
        <div v-if="shouldShowTopMetrics" class="expandable">
          <ExpandableIcon
            class="expandable-icon"
            @click="showExpandedTopVideos = true"
          />
          <div class="top-videos">
            <div class="video-list-container">
              <div class="video-list-header">
                <div class="video-column video-column-filename-title">
                  {{ $t("dashboard.topVideos.title") }}
                </div>
                <div class="video-column video-column-conversion-title">
                  {{ $t("dashboard.topVideos.conversionShort") }}
                </div>
                <div class="video-column video-column-completion-title">
                  {{ $t("dashboard.topVideos.viewCompletion") }}
                </div>
              </div>
              <div
                v-for="(video, index) in topPerformingVideos"
                :key="video.id"
                class="video-row"
              >
                <div
                  class="video-column video-column-filename"
                  :title="video.fileName"
                >
                  {{ video.fileName | truncate(30) }}
                </div>
                <div class="video-column video-column-conversion">
                  {{ video.conversionRate }}%
                </div>
                <div class="video-column video-column-completion">
                  {{ video.completionPercentage }}%
                </div>
              </div>
              <div
                v-if="!topPerformingVideos || topPerformingVideos.length === 0"
                class="no-data"
              >
                {{ $t("dashboard.topVideos.noData") }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="shouldShowTopMetrics" class="expandable">
          <ExpandableIcon
            class="expandable-icon"
            @click="showExpandedTopPages = true"
          />
          <div class="top-videos">
            <div class="video-list-container">
              <div class="video-list-header">
                <div class="video-column video-column-filename-title">
                  {{ $t("dashboard.topPages.title") }}
                </div>
                <div class="video-column video-column-conversion-title">
                  {{ $t("dashboard.topPages.revenueShort") }}
                </div>
                <div class="video-column video-column-completion-title">
                  {{ $t("dashboard.topPages.totalViewTime") }}
                </div>
              </div>
              <div
                v-for="(page, index) in topPerformingPages"
                :key="index"
                class="video-row"
              >
                <div
                  class="video-column video-column-filename"
                  :title="page.url"
                >
                  {{ page.url | truncate(30) }}
                </div>
                <div class="video-column video-column-conversion">
                  {{ getCurrency }} {{ Math.round(page.conversionAmount) }}
                </div>
                <div class="video-column video-column-completion">
                  {{ page.formattedTotalTimeWatched }}
                </div>
              </div>
              <div
                v-if="!topPerformingPages || topPerformingPages.length === 0"
                class="no-data"
              >
                {{ $t("dashboard.topVideos.noData") }}
              </div>
            </div>
          </div>
        </div>
        <div class="expandable">
          <ExpandableIcon
            class="expandable-icon"
            @click="showExpandedChart = true"
          />
          <section class="chart-frame">
            <div class="chart-container">
              <canvas id="myChart"></canvas>
            </div>
            <div class="chart-bottom">
              <div class="metric-selector">
                <template v-for="(metric, index) in frames">
                  <input
                    :id="metric.chartValue"
                    :key="metric.chartValue"
                    v-model="metricSelectorPicked"
                    :class="`button-radio ${chartColor}`"
                    type="radio"
                    name="metric-selector"
                    :value="metric.chartValue"
                  />
                  <label
                    :key="`${metric.chartName}-label`"
                    :for="metric.chartValue"
                    class="radio-label"
                    @click="selectFrame(index)"
                    >{{ $t(`${metric.chartName}`) }}</label
                  >
                </template>
              </div>
            </div>
          </section>
        </div>
        <div class="expandable expandable-funnel">
          <ExpandableIcon
            class="expandable-icon"
            @click="showExpandedFunnel = true"
          />
          <DashboardFunnel :segment-data-input="funnelData" />
        </div>
        <div v-if="shouldShowTopMetrics" class="expandable">
          <ExpandableIcon
            class="expandable-icon"
            @click="showExpandedPie = true"
          />
          <div class="pie-container">
            <div class="pie-buttons">
              <button
                v-for="(data, key) in pieChartData"
                :key="key"
                :class="{ active: currentPieKey === key }"
                @click="changePieData(key)"
              >
                <span
                  class="color-indicator"
                  :style="{
                    backgroundColor:
                      currentPieKey === key ? data.color : 'grey',
                  }"
                ></span>
                {{ getButtonLabel(key) }}
              </button>
            </div>
            <div class="pie-chart-canvas-wrapper">
              <canvas id="devicePieChart"></canvas>
              <div
                v-if="currentPieData.desktopPercentage > 0 && !isPieChartEmpty"
                class="pie-center-item"
                :style="pieLabelPositions.desktop"
              >
                <DesktopIcon class="pie-icon desktop-icon" />
                {{ currentPieData.desktopPercentage }}%
              </div>
              <div
                v-if="currentPieData.mobilePercentage > 0 && !isPieChartEmpty"
                class="pie-center-item"
                :style="pieLabelPositions.mobile"
              >
                <MobileIcon class="pie-icon" />
                {{ currentPieData.mobilePercentage }}%
              </div>
              <div v-if="isPieChartEmpty" class="no-data-pie">
                No data available
              </div>
            </div>
          </div>
        </div>
        <div v-if="shouldShowTopMetrics" class="expandable">
          <ExpandableIcon
            class="expandable-icon"
            @click="showExpandedWidgets = true"
          />
          <div class="top-videos">
            <div class="video-list-container">
              <div class="video-list-header">
                <div class="video-column video-column-filename-title">
                  {{ $t("dashboard.topWidgets.title") }}
                </div>
                <div class="video-column video-column-conversion-title">
                  {{ $t("dashboard.topWidgets.impressions") }}
                </div>
                <div class="video-column video-column-completion-title">
                  {{ $t("dashboard.topWidgets.clicks") }}
                </div>
              </div>
              <div
                v-for="(widget, index) in widgetsForExpandable"
                :key="index"
                class="video-row"
              >
                <div
                  class="video-column video-column-filename"
                  :title="widget.name"
                >
                  {{ widget.name | truncate(30) }}
                </div>
                <div class="video-column video-column-conversion">
                  {{ widget.widgetDisplays }}
                </div>
                <div class="video-column video-column-completion">
                  {{ widget.widgetClicks }}
                </div>
              </div>
              <!-- <div
                v-if="!topPerformingPages || topPerformingPages.length === 0"
                class="no-data"
              >
                {{ $t("dashboard.topVideos.noData") }}
              </div> -->
            </div>
          </div>
        </div>
      </div>
    </main>
    <ExpandedFunnel
      v-if="showExpandedFunnel"
      :segment-data-input="expandedFunnelData"
      @close="showExpandedFunnel = false"
    />
    <ExpandedTopVideos
      v-if="showExpandedTopVideos"
      :video-metrics="localVideoMetrics"
      :from-date="fromDate"
      :to-date="toDate"
      :selected-campaigns="selectedCampaigns"
      :device-selector-picked="deviceSelectorPicked"
      :is-all-campaigns-selected="isAllCampaignsSelected"
      :currency="getCurrency"
      @open-video-library-with-video="openVideoLibraryWithVideo"
      @close="showExpandedTopVideos = false"
    />
    <ExpandedTopPages
      v-if="showExpandedTopPages"
      :page-metrics="localPageMetrics"
      :from-date="fromDate"
      :to-date="toDate"
      :selected-campaigns="selectedCampaigns"
      :device-selector-picked="deviceSelectorPicked"
      :is-all-campaigns-selected="isAllCampaignsSelected"
      :currency="getCurrency"
      @close="showExpandedTopPages = false"
    />
    <small-modal
      v-if="showExpandedChart"
      class="expanded-chart-modal bigger"
      @close="
        showExpandedChart = false;
        if (modalChartInstance) {
          modalChartInstance.destroy();
        }
      "
    >
      <div class="main-wrapper-modal-chart">
        <div class="expanded-chart-header">
          <h2 class="expanded-chart-title">Graphs</h2>
          <p class="expanded-chart-subtitle">
            To adjust the period, exit this modal and pick dates from the
            top-left calendar.
          </p>
        </div>
        <div class="graph-container" :class="getChartColor">
          <section class="chart-frame">
            <div class="chart-container">
              <canvas id="myModalChart"></canvas>
            </div>
            <div class="chart-bottom">
              <div class="metric-selector">
                <template v-for="(metric, index) in frames">
                  <input
                    :id="`${metric.chartValue}-modal`"
                    :key="`${metric.chartValue}-modal`"
                    v-model="metricSelectorPicked"
                    :class="`button-radio ${chartColor}`"
                    type="radio"
                    name="metric-selector-modal"
                    :value="metric.chartValue"
                    @change="selectFrame(index)"
                  />
                  <label
                    :key="`${metric.chartName}-label-modal`"
                    :for="`${metric.chartValue}-modal`"
                    class="radio-label"
                    @click="selectFrame(index)"
                    >{{ $t(metric.chartName) }}</label
                  >
                </template>
              </div>
            </div>
          </section>
        </div>
      </div>
      <dashboard-frames
        :dashboard-data="dashboardGlobalData"
        :frames="frames"
        :currency="getCurrency"
        :selected-frame="selectedFrame"
        :is-expanded-chart="true"
        @selectedFrame="selectFrame($event)"
      />
    </small-modal>
    <ExpandedPieChart
      v-if="showExpandedPie"
      :provisional-pie-data="pieChartData"
      :current-pie-key="currentPieKey"
      @close="showExpandedPie = false"
      @change-pie-data="changePieData"
    />
    <ExpandedWidgets
      v-if="showExpandedWidgets"
      :widgets="widgetMetrics"
      :currency="getCurrency"
      :from-date="fromDate"
      :to-date="toDate"
      @close="showExpandedWidgets = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import Chart from "chart.js";
import enums from "@/enums";
import throttle from "lodash/throttle";
import debounce from "lodash/debounce";
import Deselect from "@/components/Deselect";
import BannerLocked from "@/components/shared/BannerLocked.vue";
import DashboardDatepicker from "@/components/dashboard/DashboardDatepicker.vue";
import DashboardFrames from "@/components/dashboard/DashboardFrames";
import createGradients from "@/components/dashboard/createGradients";
import chartConfig from "@/components/dashboard/chartConfig";
import endOfToday from "date-fns/end_of_today";
import isEqual from "date-fns/is_equal";
import OpenIndicator from "@/assets/svg/magnifying-glass.svg?inline";
import metricFrames from "../../utils/metric-frames.js";
import VidjetHeader from "@/components/shared/Vidjet-header";
import DashboardFunnel from "./DashboardFunnel.vue";
import ExportIcon from "@/assets/svg/export-icon.svg?inline";
import ExpandableIcon from "@/assets/svg/expandable-icon.svg?inline";
import ExpandedFunnel from "@/components/dashboard/ExpandedFunnel.vue";
import ExpandedTopVideos from "@/components/dashboard/ExpandedTopVideos.vue";
import ExpandedTopPages from "@/components/dashboard/ExpandedTopPages.vue";
import SmallModal from "@/components/shared/SmallModal.vue";
import DashboardSkeleton from "@/components/dashboard/DashboardSkeleton.vue";
import DesktopIcon from "@/assets/svg/small-desktop-icon.svg?inline";
import MobileIcon from "@/assets/svg/small-mobile-icon.svg?inline";
import ExpandedPieChart from "@/components/dashboard/ExpandedPieChart.vue";
import ExpandedWidgets from "@/components/dashboard/ExpandedWidgets.vue";

const { StateFlag } = enums;

export default {
  name: "Dashboard",
  components: {
    BannerLocked,
    DashboardDatepicker,
    DashboardFrames,
    VidjetHeader,
    DashboardFunnel,
    ExportIcon,
    ExpandableIcon,
    ExpandedFunnel,
    ExpandedTopVideos,
    SmallModal,
    ExpandedTopPages,
    DashboardSkeleton,
    DesktopIcon,
    MobileIcon,
    ExpandedPieChart,
    ExpandedWidgets,
  },
  props: {
    isAgency: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fromDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      toDate: endOfToday(),
      frames: [],
      selectedCampaigns: this.isAgency
        ? [{ name: this.$t("agencyPortal.allDomains") }]
        : [
            {
              name: this.$t("dashboard.filters.campaignsSelector.allCampaigns"),
            },
          ],
      deviceSelectorPicked: "allDevices",
      metricSelectorPicked: "widgetDisplays",
      firstInitDone: false,
      chart: {},
      chartData: [],
      campaigns: [],
      queryMetrics: {},
      allMetricsLoading: true,
      borderColors: ["#E3B526", "#0EBA0C", "#0030D3", "#B5030E", "#7B01B5"],
      gradientsArray: [],
      allCampaignsGradientObject: {},
      StateFlag,
      Deselect,
      OpenIndicator,
      selectedFrame: 1,
      chartColor: "darkPurple",
      graphColor: {
        darkBlue: "#400081",
        darkPurple: "#6e15ba",
        lightPink: "#a328c1",
      },
      showVideoLibrary: false,
      videoLibraryCreation: false,
      showWidgetCreator: false,
      showExpandedFunnel: false,
      localVideoMetrics: [],
      localPageMetrics: [],
      showExpandedTopVideos: false,
      showExpandedTopPages: false,
      showExpandedChart: false,
      modalChartInstance: null,
      videoFileNameToFilter: "",
      pieChart: null,
      showExpandedPie: false,
      provisionalPieData: {
        widgetImpressions: {
          mobileData: 300,
          desktopData: 700,
          mobilePercentage: 30,
          desktopPercentage: 70,
          color: "#E789FF",
        },
        widgetClicks: {
          mobileData: 450,
          desktopData: 550,
          mobilePercentage: 45,
          desktopPercentage: 55,
          color: "#A415BA",
        },
        salesGenerated: {
          mobileData: 250,
          desktopData: 750,
          mobilePercentage: 25,
          desktopPercentage: 75,
          color: "#7B01B5",
        },
      },
      currentPieKey: "salesGenerated",
      devicePieChart: null,
      pieLabelPositions: {
        desktop: { top: "50%", left: "50%" },
        mobile: { top: "50%", left: "50%" },
      },
      showExpandedWidgets: false,
      deviceMetrics: null,
      widgetMetrics: null,
    };
  },
  computed: {
    ...mapState(["plan", "site"]),
    ...mapGetters({
      bannerLocked: "plan/bannerLocked",
      getCurrency: "site/getCurrency",
    }),
    ...mapState({
      graphData: (state) => state.dashboard.dashboardGraphMetrics,
      dashboardGlobalData: (state) => state.dashboard.dashboardGlobalMetrics,
      dataTable: (state) => state.dashboard.dashboardCampaignMetrics,
    }),
    campaignOptions() {
      if (this.isAgency) {
        return this.site.agencySites.map((site) => ({
          name: site.websiteUrl,
          _id: site.siteId,
        }));
      }
      return [
        { name: this.$t("dashboard.filters.campaignsSelector.allCampaigns") },
        ...this.campaigns,
      ];
    },
    isAllCampaignsSelected() {
      if (this.isAgency) {
        return !this.selectedCampaigns.some(
          (domain) => domain.name !== this.$t("agencyPortal.allDomains")
        );
      } else {
        return Boolean(
          this.selectedCampaigns.filter(
            (campaign) =>
              campaign.name ===
              this.$t("dashboard.filters.campaignsSelector.allCampaigns")
          ).length
        );
      }
    },
    getChartColor() {
      return `is-selected-${this.chartColor}`;
    },

    userIsShopify() {
      return (
        this.site.site.integration === "shopify" && !this.site.site.payByCard
      );
    },
    filteredVideos() {
      if (!this.dataTable || !this.dataTable.videos) {
        return [];
      }

      const uniqueVideos = {};
      return this.dataTable.videos
        .filter((video) => !video.isPremade)
        .filter((video) => {
          if (uniqueVideos[video.fileName]) {
            return false;
          }
          uniqueVideos[video.fileName] = true;
          return true;
        });
    },
    filteredLocalVideoMetrics() {
      if (!this.localVideoMetrics || this.localVideoMetrics.length === 0) {
        return [];
      }
      return this.localVideoMetrics.filter((video) => video.isPremade !== true);
    },
    topPerformingVideos() {
      if (
        !this.filteredLocalVideoMetrics ||
        this.filteredLocalVideoMetrics.length === 0
      ) {
        return [];
      }
      const sortedVideos = [...this.filteredLocalVideoMetrics].sort((a, b) => {
        const rateA = Number(a.conversionRate) || 0;
        const rateB = Number(b.conversionRate) || 0;
        return rateB - rateA;
      });
      return sortedVideos.slice(0, 5);
    },

    topPerformingPages() {
      if (!this.localPageMetrics || this.localPageMetrics.length === 0) {
        return [];
      }

      const sortedPages = [...this.localPageMetrics].sort((a, b) => {
        const amountA = Number(a.conversionAmount) || 0;
        const amountB = Number(b.conversionAmount) || 0;
        return amountB - amountA;
      });

      const top4Pages = sortedPages.slice(0, 5);

      return top4Pages.map((page) => {
        return {
          ...page,
          url: page.url,
          formattedConversionAmount: `$${(
            Number(page.conversionAmount) || 0
          ).toFixed(2)}`,
          formattedTotalTimeWatched: this.formatMinutesToHoursAndMinutes(
            page.totalTimeWatched
          ),
        };
      });
    },
    funnelData() {
      return [
        {
          id: "visits",
          title: this.$t("dashboard.funnel.totalVisits"),
          value: this.dashboardGlobalData.totalVisitors,
          color: "#F8BFFF",
        },
        {
          id: "views",
          title: this.$t("dashboard.funnel.widgetViews"),
          value: this.dashboardGlobalData.widgetDisplays,
          color: "#E789FF",
        },
        {
          id: "clicks",
          title: this.$t("dashboard.funnel.widgetClicks"),
          value: this.dashboardGlobalData.widgetClicks,
          color: "#A415BA",
        },
        {
          id: "sales",
          title: this.$t("dashboard.funnel.salesGenerated"),
          value: this.dashboardGlobalData.totalSalesCount,
          color: "#7B01B5",
        },
      ];
    },
    expandedFunnelData() {
      return [
        {
          id: "visits",
          title: this.$t("dashboard.funnel.totalVisitsWebsite"),
          value: this.dashboardGlobalData.totalVisitors,
          color: "#F8BFFF",
        },
        {
          id: "views",
          title: this.$t("dashboard.funnel.widgetViews"),
          value: this.dashboardGlobalData.widgetDisplays,
          color: "#E789FF",
        },
        {
          id: "clicks",
          title: this.$t("dashboard.funnel.widgetClicks"),
          value: this.dashboardGlobalData.widgetClicks,
          color: "#A415BA",
        },
        {
          id: "sales",
          title: this.$t("dashboard.funnel.salesGeneratedVideos"),
          value: this.dashboardGlobalData.totalSalesCount,
          color: "#7B01B5",
        },
      ];
    },
    shouldShowTopMetrics() {
      if (!this.isAgency) return true;
      return this.selectedCampaigns.some(
        (c) => c.name !== this.$t("agencyPortal.allDomains")
      );
    },
    pieChartData() {
      if (!this.deviceMetrics) {
        return {
          widgetDisplays: {
            mobileData: 0,
            desktopData: 0,
            mobilePercentage: 0,
            desktopPercentage: 0,
            color: "#E789FF",
          },
          salesGenerated: {
            mobileData: 0,
            desktopData: 0,
            mobilePercentage: 0,
            desktopPercentage: 0,
            color: "#7B01B5",
          },
          videosPlayed: {
            mobileData: 0,
            desktopData: 0,
            mobilePercentage: 0,
            desktopPercentage: 0,
            color: "#A415BA",
          },
        };
      }

      return {
        salesGenerated: {
          ...this.deviceMetrics.salesGenerated,
          color: "#7B01B5",
        },
        widgetDisplays: {
          ...this.deviceMetrics.widgetDisplays,
          color: "#E789FF",
        },
        videosPlayed: {
          ...this.deviceMetrics.videosPlayed,
          color: "#A415BA",
        },
      };
    },
    currentPieData() {
      return this.pieChartData[this.currentPieKey];
    },
    widgetsForExpandable() {
      if (this.widgetMetrics) {
        return this.widgetMetrics.slice(0, 5);
      } else return [];
    },
    isPieChartEmpty() {
      return (
        this.currentPieData.desktopData === 0 &&
        this.currentPieData.mobileData === 0
      );
    },
  },

  watch: {
    // Only the change of campaigns or device triggers a refetch
    async deviceSelectorPicked() {
      await this.throttledFetch();
      this.updateChart(this.metricSelectorPicked);
    },
    async metricSelectorPicked() {
      this.updateChart(this.metricSelectorPicked);
    },
    async selectedCampaigns() {
      await this.debouncedFetch();
      this.updateChart(this.metricSelectorPicked);
    },
    showExpandedChart(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const modalCtx = document
            .getElementById("myModalChart")
            .getContext("2d");

          const clonedData = {
            labels: [...this.chart.data.labels],
            datasets: this.chart.data.datasets.map((dataset) => ({
              ...dataset,
              _meta: undefined,
              _chart: undefined,
              backgroundColor: this.regenerateGradients(modalCtx, dataset),
            })),
          };

          this.modalChartInstance = new Chart(modalCtx, {
            type: "line",
            data: clonedData,
            options: { ...chartConfig().options },
          });
        });
      } else {
        if (this.modalChartInstance) {
          this.modalChartInstance.destroy();
          this.modalChartInstance = null;
        }
      }
    },
  },

  async mounted() {
    // Retreive all campaign informations
    await this.getAllCampaigns();
    // The chart elements needs to be present before we fetch the data
    this.displayChart();
    await this.fetchData();
    this.initDevicePieChart();
  },
  async created() {
    //Make sure to clean all campaign state so we do not have issue on new create/edit
    this.$store.commit("campaign/resetCampaignState");
    this.$store.commit("video/resetVideos");

    // We load the properties from the locale file
    this.frames = metricFrames;
    await this.$store.dispatch("plan/getPlan", {
      siteId: this.$store.state.account.user.siteId,
    });
    await this.$store.dispatch("site/getSite", {
      siteId: this.$store.state.account.user.siteId,
    });
    if (this.userIsShopify && this.dashboardGlobalData.totalSales) {
      this.selectFrame(0);
    }
    if (this.$route.query.action === "videoLibraryCreation") {
      this.videoLibraryCreation = true;
    }
  },

  methods: {
    ...mapActions("dashboard", [
      "dashboardVideoMetrics",
      "dashboardPageMetrics",
      "exportDashboardDataAction",
    ]),

    handleInputChange() {
      if (this.isAgency) {
        // Handle agencySites logic
        this.selectedCampaigns = this.selectedCampaigns.filter(
          (site) => site.name !== this.$t("agencyPortal.allDomains")
        );

        // Ensure at least one site is selected
        if (!this.selectedCampaigns.length) {
          this.selectedCampaigns.push({
            name: this.$t("agencyPortal.allDomains"),
          });
        }
      } else {
        // Handle campaigns logic
        this.selectedCampaigns = this.selectedCampaigns.filter(
          (campaign) =>
            campaign.name !==
            this.$t("dashboard.filters.campaignsSelector.allCampaigns")
        );

        // Ensure at least one campaign is selected
        if (!this.selectedCampaigns.length) {
          this.selectedCampaigns.push({
            name: this.$t("dashboard.filters.campaignsSelector.allCampaigns"),
          });
        }
      }
    },
    // This just sets up the foundation of the chart, we update it when data
    // changes with updateChart().
    displayChart() {
      const ctx = document.getElementById("myChart").getContext("2d");

      this.gradientsArray = createGradients.singleCampaignGradient(ctx);
      this.allCampaignsGradientObject =
        createGradients.allCampaignsGradient(ctx);

      this.chart = new Chart(ctx, chartConfig(this.chartData));
    },

    getName(id) {
      return this.selectedCampaigns.find(
        (campaignObject) => campaignObject._id === id
      ).name;
    },

    updateChart(selectedChartMetric) {
      let campaignDataToDraw = this.graphData;
      if (!this.chart.data || !campaignDataToDraw) return;

      if (this.isAllCampaignsSelected) {
        this.chart.data.labels = getDays(campaignDataToDraw);
        this.chartData = {
          label: this.$t("dashboard.filters.campaignsSelector.allCampaigns"),
          data: this.getMetricArray(this.graphData, selectedChartMetric),
          borderColor: this.graphColor[this.chartColor],
          borderWidth: 1,
          fill: true,
          backgroundColor: this.allCampaignsGradientObject[this.chartColor],
          pointRadius: 3,
          pointHitRadius: 0,
          pointBackgroundColor: this.graphColor[this.chartColor],
        };
      } else {
        let selectedIds = this.selectedCampaigns.map(
          (campaign) => campaign._id
        );
        this.chartData = selectedIds.map((id, index) => {
          let filteredById = campaignDataToDraw.filter((campaignData) =>
            this.isAgency
              ? campaignData.siteId === id
              : campaignData.campaignId === id
          );
          this.chart.data.labels = getDays(filteredById);
          return {
            label: `${this.getName(id)}`,
            data: this.getMetricArray(filteredById, selectedChartMetric),
            borderColor: this.borderColors[index],
            borderWidth: 1,
            fill: true,
            backgroundColor: this.gradientsArray[index],
            pointRadius: 3,
            pointHitRadius: 0,
            pointBackgroundColor: this.borderColors[index],
          };
        });
      }

      function updateViewTimeTicks() {
        return function (value) {
          return selectedChartMetric === "avgViewTime" ? `${value}s` : value;
        };
      }

      function updateViewTimeTooltip() {
        return function (tooltipItem, data) {
          let label = data.datasets[tooltipItem.datasetIndex].label || "";
          if (label) label += ": ";
          label += Math.round(tooltipItem.yLabel * 100) / 100;
          label += selectedChartMetric === "avgViewTime" ? "s" : "";
          return label;
        };
      }

      function getDays(campaignArray) {
        return campaignArray.map((e) => e.day);
      }

      this.isAllCampaignsSelected
        ? (this.chart.data.datasets[0] = this.chartData)
        : (this.chart.data.datasets = this.chartData);

      this.chart.options.scales.yAxes[0].ticks.callback = updateViewTimeTicks();
      this.chart.options.tooltips.callbacks.label = updateViewTimeTooltip();
      this.chart.update();

      if (this.modalChartInstance) {
        const modalCtx = this.modalChartInstance.ctx;
        const modalGradients = createGradients.singleCampaignGradient(modalCtx);
        const modalAllCampaignsGradient =
          createGradients.allCampaignsGradient(modalCtx);

        this.modalChartInstance.data = {
          labels: [...this.chart.data.labels],
          datasets: this.chart.data.datasets.map((dataset) => ({
            ...dataset,
            _meta: undefined,
            _chart: undefined,
            backgroundColor: this.isAllCampaignsSelected
              ? modalAllCampaignsGradient[this.chartColor]
              : modalGradients[this.borderColors.indexOf(dataset.borderColor)],
          })),
        };

        this.modalChartInstance.options = JSON.parse(
          JSON.stringify(this.chart.options)
        );
        this.modalChartInstance.update();
      }
    },

    getMetricArray(inputArray, metric) {
      return inputArray.map((element) => element[metric]);
    },

    debouncedFetch: debounce(async function () {
      await this.fetchData();
    }, 1000),

    throttledFetch: throttle(async function () {
      await this.fetchData();
    }, 1000),

    async updateData(from, to) {
      this.fromDate = from;
      this.toDate = to;
      this.debouncedFetch();
    },

    async getAllCampaigns() {
      this.campaigns = [
        ...(await this.$store.dispatch("campaign/getCampaigns", {
          siteId: this.$store.state.account.user.siteId,
        })),
      ];
    },
    async fetchData() {
      try {
        this.allMetricsLoading = true;

        // We need to add a day to the end date, so it includes the full day
        // Until 23:59 at night
        let fullDayToDate = new Date(this.toDate);

        // We only add the 23:59 if the user selects a date.
        // Because we have an endOfToday() helper that doesn't work when we
        // select a date manually due to timezone offset.
        if (!isEqual(fullDayToDate, endOfToday())) {
          fullDayToDate.setHours(fullDayToDate.getHours() + 23);
          fullDayToDate.setMinutes(fullDayToDate.getMinutes() + 59);
        }

        // Create the query metrics
        // We only send the ids as params when some campaigns or domains are selected
        const specificQueryMetrics = this.isAgency
          ? {
              ...(this.shouldShowTopMetrics && {
                siteIds: this.selectedCampaigns.map((site) => site._id),
              }),
              agencyId: this.$store.state.account.user.siteId,
            }
          : {
              ...(!this.isAllCampaignsSelected && {
                campaignId: this.selectedCampaigns.map(
                  (campaign) => campaign._id
                ),
              }),
              siteId: this.$store.state.account.user.siteId,
            };

        this.queryMetrics = {
          ...(this.deviceSelectorPicked !== "allDevices" && {
            device: this.deviceSelectorPicked,
          }),
          fromDate: new Date(this.fromDate).toISOString(),
          toDate: fullDayToDate.toISOString(),
          ...specificQueryMetrics,
        };

        // Retrieve global metrics
        await this.$store.dispatch("dashboard/dashboardGlobalMetrics", {
          query: this.queryMetrics,
          isAgency: this.isAgency,
        });

        // Retrieve Graph metrics
        await this.$store.dispatch("dashboard/dashboardGraphMetrics", {
          query: this.queryMetrics,
          isAgency: this.isAgency,
        });

        await this.$store.dispatch(
          "dashboard/fetchDashboardDeviceMetrics",
          this.queryMetrics
        );

        this.deviceMetrics = this.$store.state.dashboard.dashboardDeviceMetrics;

        await this.$store.dispatch(
          "dashboard/fetchDashboardWidgetMetrics",
          this.queryMetrics
        );

        this.widgetMetrics = this.$store.state.dashboard.dashboardWidgetMetrics;

        if (!this.isAgency) {
          await this.dashboardVideoMetrics(this.queryMetrics);
          this.localVideoMetrics =
            this.$store.state.dashboard.dashboardVideoMetrics || [];

          await this.dashboardPageMetrics(this.queryMetrics);
          this.localPageMetrics =
            this.$store.state.dashboard.dashboardPageMetrics || [];
        } else {
          if (this.shouldShowTopMetrics) {
            await this.$store.dispatch("dashboard/agencyVideoMetrics", {
              ...this.queryMetrics,
              siteIds: this.selectedCampaigns.map((site) => site._id),
            });
            this.localVideoMetrics =
              this.$store.state.dashboard.agencyVideoMetrics || [];

            await this.$store.dispatch("dashboard/agencyPageMetrics", {
              ...this.queryMetrics,
              siteIds: this.selectedCampaigns.map((site) => site._id),
            });
            this.localPageMetrics =
              this.$store.state.dashboard.agencyPageMetrics || [];
          } else {
            this.localVideoMetrics = [];
            this.localPageMetrics = [];
          }
        }

        this.firstInitDone = true;
        this.updateChart(this.metricSelectorPicked);

        this.allMetricsLoading = false;
      } catch (err) {
        console.log(err);
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: err,
          type: "error",
        });
      }
    },

    async goToEditCampaign(campaignId) {
      await this.$store.dispatch("campaign/setCampaignSelected", campaignId);
      this.$router.push({ name: "editCampaign" });
    },
    getStatus(campaign) {
      return campaign.stateFlag === StateFlag.active ? "LIVE" : "INACTIVE";
    },
    selectFrame(index) {
      this.selectedFrame = index;
      this.metricSelectorPicked = this.frames[index].chartValue;
      this.chartColor = this.frames[index].color;
    },
    closeVideoLibrary() {
      this.showVideoLibrary = false;
      this.videoLibraryCreation = false;
      this.videoFileNameToFilter = "";
    },
    formatMinutesToHoursAndMinutes(totalMinutes) {
      if (
        totalMinutes === null ||
        totalMinutes === undefined ||
        isNaN(Number(totalMinutes))
      ) {
        return "0 h 00 min";
      }
      const minutes = Number(totalMinutes);
      if (minutes < 0) return "0 h 00 min";

      const hours = Math.floor(minutes / 60);
      const remainingMinutes = Math.round(minutes % 60);

      const paddedMinutes =
        remainingMinutes < 10 ? `0${remainingMinutes}` : remainingMinutes;

      return `${hours} h ${paddedMinutes} min`;
    },
    regenerateGradients(ctx, dataset) {
      if (this.isAllCampaignsSelected) {
        return createGradients.allCampaignsGradient(ctx)[this.chartColor];
      }
      return createGradients.singleCampaignGradient(ctx)[
        this.borderColors.indexOf(dataset.borderColor)
      ];
    },
    async handleExportData() {
      let fullDayToDate = new Date(this.toDate);
      if (!isEqual(fullDayToDate, endOfToday())) {
        fullDayToDate.setHours(23, 59, 59, 999);
      }

      const exportParams = {
        fromDate: new Date(this.fromDate).toISOString(),
        toDate: fullDayToDate.toISOString(),
        siteId: this.$store.state.account.user.siteId,
        exportType: "global",
      };

      if (!this.isAllCampaignsSelected) {
        if (
          this.selectedCampaigns.length > 0 &&
          this.selectedCampaigns[0]._id
        ) {
          exportParams.campaignId = this.selectedCampaigns.map((c) => c._id);
        }
      }

      if (this.deviceSelectorPicked !== "allDevices") {
        exportParams.device = this.deviceSelectorPicked;
      }

      try {
        const result = await this.exportDashboardDataAction(exportParams);
        return result;
      } catch (error) {
        console.error(error);
      }
    },
    openVideoLibraryWithVideo(fileName) {
      this.videoFileNameToFilter = fileName;
      this.showVideoLibrary = true;
    },
    changePieData(key) {
      this.currentPieKey = key;
      this.updateDevicePieChart();
    },

    getButtonLabel(key) {
      const labels = {
        videosPlayed: "Videos played",
        widgetDisplays: "Widget impressions",
        salesGenerated: "Sales Generated",
      };
      return this.$t(`${labels[key]}`);
    },

    hexToRgba(hex, alpha) {
      let r = 0,
        g = 0,
        b = 0;
      if (hex.length === 4) {
        r = "0x" + hex[1] + hex[1];
        g = "0x" + hex[2] + hex[2];
        b = "0x" + hex[3] + hex[3];
      } else if (hex.length === 7) {
        r = "0x" + hex[1] + hex[2];
        g = "0x" + hex[3] + hex[4];
        b = "0x" + hex[5] + hex[6];
      }
      return `rgba(${+r},${+g},${+b},${alpha})`;
    },

    updatePieLabelPositions() {
      const chart = this.devicePieChart;
      if (!chart) return;
      const meta = chart.getDatasetMeta(0);
      if (!meta.data || meta.data.length < 2) return;

      if (this.currentPieData.desktopPercentage === 100) {
        this.pieLabelPositions = {
          desktop: { top: "50%", left: "50%" },
          mobile: { display: "none" },
        };
        return;
      }

      if (this.currentPieData.mobilePercentage === 100) {
        this.pieLabelPositions = {
          desktop: { display: "none" },
          mobile: { top: "50%", left: "50%" },
        };
        return;
      }

      const calculatePosition = (slice) => {
        if (!slice || !slice._model) return { top: "50%", left: "50%" };
        const model = slice._model;
        const angle =
          model.startAngle + (model.endAngle - model.startAngle) / 2;
        const radius =
          model.innerRadius + (model.outerRadius - model.innerRadius) / 2;
        const x = model.x + Math.cos(angle) * radius;
        const y = model.y + Math.sin(angle) * radius;
        return { top: `${y}px`, left: `${x}px` };
      };

      this.pieLabelPositions = {
        desktop: calculatePosition(meta.data[0]),
        mobile: calculatePosition(meta.data[1]),
      };
    },

    getPieChartDataConfig() {
      if (this.isPieChartEmpty) {
        return {
          data: [100],
          backgroundColor: ["#C4C4C4"],
          borderWidth: 0,
        };
      } else if (this.currentPieData.desktopPercentage === 100) {
        return {
          data: [100],
          backgroundColor: [this.currentPieData.color],
          borderWidth: 0,
        };
      } else if (this.currentPieData.mobilePercentage === 100) {
        return {
          data: [100],
          backgroundColor: [this.currentPieData.color],
          borderWidth: 0,
        };
      } else {
        return {
          data: [
            this.currentPieData.desktopData,
            this.currentPieData.mobileData,
          ],
          backgroundColor: [
            this.hexToRgba(this.currentPieData.color, 0.3),
            this.currentPieData.color,
          ],
          borderWidth: 4,
        };
      }
    },

    initDevicePieChart() {
      const ctx = document.getElementById("devicePieChart").getContext("2d");
      const pieDataConfig = this.getPieChartDataConfig();

      this.devicePieChart = new Chart(ctx, {
        type: "pie",
        data: {
          labels: ["Desktop", "Mobile"],
          datasets: [
            {
              ...pieDataConfig,
              borderColor: "white",
              hoverBorderColor: "white",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: { enabled: false },
          cutout: "60%",
          animation: {
            onComplete: () => this.updatePieLabelPositions(),
          },
          onResize: () => this.updatePieLabelPositions(),
        },
      });
    },

    updateDevicePieChart() {
      if (this.devicePieChart) {
        const pieDataConfig = this.getPieChartDataConfig();

        this.devicePieChart.data.datasets[0].data = pieDataConfig.data;
        this.devicePieChart.data.datasets[0].backgroundColor =
          pieDataConfig.backgroundColor;
        this.devicePieChart.data.datasets[0].borderWidth =
          pieDataConfig.borderWidth;

        this.devicePieChart.update();
      }
    },
  },
};
</script>

<style lang="scss">
/* We need this additional tag to override the Multiselect component default styles */
.asd__action-buttons {
  button {
    &:nth-of-type(2) {
      color: #40916c !important;
    }
  }
}
.campaign-select-input {
  &.all-campaigns {
    .vs__selected {
      &:nth-of-type(1) {
        @include selected($light-grey);
      }
    }
  }
}
.vs--open .vs__dropdown-toggle {
  border-bottom: 1px solid #c4c4c4 !important;
  border-radius: 3px !important;
}
.main-content-wrapper {
  margin-right: 30px;
  margin-left: 100px;
}

.main-content-wrapper-agency {
  padding-top: 8px;
}
.campaign-select-input {
  .vs__search::placeholder,
  .vs__dropdown-toggle,
  .vs__dropdown-menu {
    background: white;
    color: $light-grey;
    border: 1px solid $light-grey;
    box-sizing: border-box;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.12);
    border-radius: 3px;
    @include base-font;
    @include font-small;
  }

  .vs__dropdown-menu {
    position: absolute !important;
  }
  .vs__dropdown-toggle {
    height: 30px;
    position: relative;
  }
  .vs__actions {
    position: absolute;
    right: 0;
    top: 5px;
  }
  .vs__selected-options {
    @include media("<tablet") {
      left: 0;
      top: 32px;
    }
  }
  .vs__selected {
    @include font-smaller;
    display: flex;
    align-items: center;
    font-weight: 500;
    padding: 1px 3px;
    svg {
      path {
        fill: inherit;
      }
    }
    &:nth-of-type(1) {
      @include selected($yellow);
    }
    &:nth-of-type(2) {
      @include selected($green);
    }
    &:nth-of-type(3) {
      @include selected($blue);
    }
    &:nth-of-type(4) {
      @include selected($red);
    }
  }
  .vs__dropdown-menu {
    margin-top: 8px;
    padding: 12px 4px;
    .vs__dropdown-option {
      @include base-font;
      @include font-smaller;
      color: $light-grey;
      border-radius: 4px;
      padding: 4px 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .circle-checked {
        opacity: 0;
        color: $dark-purple;
      }
      &--highlight {
        background: rgba(226, 124, 252, 0.1);
      }
      &--selected {
        .circle-checked {
          opacity: 1;
        }
      }
    }
  }
  .vs__deselect {
    margin-left: 7px;
    svg {
      height: 14px;
      width: 14px;
    }
  }
  .vs__open-indicator {
    transform: none !important;
  }
}
.placeholder {
  @include font-small;
  color: $light-grey;
  margin-top: 3px;
}

.expanded-chart-modal .small-modal.bigger {
  width: 90vw;
  max-width: 90vw;
  height: 90vh;
  max-height: 90vh;
  overflow: auto;
}

.expanded-chart-modal .chart-container {
  height: 40vh !important;
  width: 100% !important;
}
</style>
<style lang="scss" scoped>
.fixed-dashboard {
  position: fixed;
  z-index: 350;
  .main-content-wrapper {
    position: fixed;
    left: 37px;
    margin-left: 63px;
  }
}
.frames-loader-wrapper {
  position: relative;
  width: 100%;
  min-height: 500px;
}

svg {
  path {
    fill: currentColor;
  }
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
  .button-primary {
    &.create-button {
      position: absolute;
      right: 5%;
      background-color: $dark-purple;
      color: white;
      z-index: 1;
      &:hover {
        background: $light-pink;
        color: black;
      }
      @include media("<=tablet") {
        display: none;
      }
    }
  }
}
.dashboard-filters {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  @include media("<=desktop") {
    display: block;
  }
  @media (min-width: 1500px) {
    justify-content: flex-start;
    gap: 32px;
  }
  .campaign-select-wrapper {
    min-width: 360px;
    width: fit-content;
    @include media("<=desktop") {
      margin: 32px 0;
    }
    @media (min-width: 1500px) {
      min-width: 480px;
    }
  }
}

.chart-frame {
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .chart-selectors {
    width: 100%;
    display: flex;
    align-items: center;
    margin-top: 17px;
    margin-bottom: 8px;
    margin-left: 120px;
  }
  .stacked-average-selector {
    .radio-label:first-of-type {
      padding-right: 18px;
    }
  }
  .chart-bottom {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-bottom: 14px;

    .metric-selector {
      @include flex-centered;
      .button-radio {
        @include font-supersmall;
        margin-right: 35px;
        & + *::before {
          font-weight: 600;
          background: $light-grey;
          border-color: $light-grey;
          width: 5px;
          height: 5px;
        }
        &.darkPurple:checked + *::before {
          background: $graph-purple;
          border-color: $graph-purple;
        }
        &.darkBlue:checked + *::before {
          background: $graph-blue;
          border-color: $graph-blue;
        }
        &.lightPink:checked + *::before {
          background: $graph-pink;
          border-color: $graph-pink;
        }

        &.darkPurple:checked + * {
          color: $graph-purple;
        }
        &.darkBlue:checked + * {
          color: $graph-blue;
        }
        &.lightPink:checked + * {
          color: $graph-pink;
        }
      }
      .radio-label {
        font-weight: 600;
        line-height: 7px;
        margin-right: 42px;
        @include media(">=tablet", "<desktop") {
          margin-right: 22px;
        }
        @include media("<tablet") {
          margin-right: 0px;
        }
      }
    }
  }
  .chart-container {
    height: 300px;
    width: 100%;
    @include media("<desktop") {
      height: 300px;
    }
  }
}
.is-selected-darkBlue {
  border: 6px solid $graph-blue;
}
.is-selected-darkPurple {
  border: 6px solid $graph-purple;
}
.is-selected-lightPink {
  border: 6px solid $graph-pink;
}
.search-icon {
  align-self: flex-end;
}
.data-table-frame {
  @include frame;
  padding: 20px;
  margin-bottom: 40px;
}

.export-button {
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  height: 30px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid $light-grey;
  color: $light-grey;
  background: white;
  border-radius: 3px;

  &:hover {
    border: 1px solid $dark-purple;
    background: $light-pink;
  }
}

.expandable-wrapper {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  margin-bottom: 60px;
}

.expandable {
  border: 2px solid $dark-purple;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  position: relative;
  box-sizing: border-box;
  &:hover {
    outline: 1px solid $dark-purple;
  }

  .chart-frame {
    width: 100%;
    height: 220px;
    display: flex;
    flex-direction: column;
    background: white;
  }
}

.expandable-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  z-index: 90;
  path {
    fill: $dark-purple;
  }
  &:hover {
    transform: scale(1.1);
  }
}

.expandable-funnel {
  padding-bottom: 16px;
}

.top-videos {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  height: 100%;

  .video-list-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
  }

  .video-list-header,
  .video-row {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 24px;
    padding: 8px 0;
  }

  .video-list-header {
    font-weight: bold;
    padding-bottom: 12px;
  }

  .video-row {
    &:last-child {
      border-bottom: none;
    }
  }

  .video-column {
    text-align: left;
    overflow: hidden;
  }

  .video-column-filename-title {
    @include base-font;
    @include font-small;
    font-weight: 600;
    color: $dark-purple;
  }

  .video-column-filename {
    @include base-font;
    @include font-smaller;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .video-column-conversion-title,
  .video-column-completion-title {
    @include base-font;
    @include font-small;
    font-weight: 400;
    color: $dark-purple;
    white-space: nowrap;
    text-align: right;
  }

  .video-column-conversion,
  .video-column-completion {
    @include base-font;
    @include font-smaller;
    font-weight: 500;
    text-align: right;
    white-space: nowrap;
  }
}

.graph-container {
  width: 100%;
  border-radius: 8px;
}

.main-wrapper-modal-chart {
  width: 90%;
  margin: 32px auto;
}

.expanded-chart-title {
  @include base-font;
  @include font-big;
  font-weight: 600;
  margin-bottom: 12px;
}
.expanded-chart-subtitle {
  @include base-font;
  @include font-normal;
  font-weight: 400;
  margin-bottom: 24px;
}

.dashboard-skeleton {
  z-index: 9000;
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.pie-container {
  display: flex;
  width: 100%;
  height: 100%;
}

.pie-buttons {
  width: 46%;
  display: flex;
  flex-direction: column;
  padding-right: 20px;
  justify-content: center;

  button {
    @include base-font;
    @include font-smaller;
    font-weight: 600;
    display: flex;
    align-items: center;
    padding: 10px 15px;
    margin-bottom: 10px;
    text-align: left;
    background: white;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.3s;
    color: $medium-grey;

    &.active {
      color: $dark-grey;
    }
  }
}

.color-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

.pie-chart-canvas-wrapper {
  position: relative;
  width: 54%;
  height: 90%;
  margin-top: 2%;
}

.pie-center-item {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #333;
  pointer-events: none;
  transition: top 0.5s ease, left 0.5s ease;
}

.pie-icon {
  margin-right: 5px;
}

.desktop-icon {
  margin-top: 5px;
}

.no-data-pie {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-weight: bold;
  color: #333;
  pointer-events: none;
}
</style>
